import React, { createContext, Dispatch, SetStateAction } from "react";
import { SpanTree } from "./structure/span/span-tree";
import { UUID } from "./model/uuid";
import { EntityMentionSet } from "./model/entities";
import { AnnotationMetadata } from "./model/metadata";
import { SituationMentionSet } from "./model/situations";
import { Situation as SituationIntermediate } from "./structure/intermediates";
import { MentionModel, ToastType } from "./communication";

type SpanTreeContextModel = Map<string, SpanTree>;
export const SpanTreeContext = createContext<SpanTreeContextModel>(
	new Map<string, SpanTree>()
);

interface TokenizationContextModel {
	highlightedMentions: Set<string>;
	activeTypeIds: UUID[];
	entityMentionSetList: EntityMentionSet[];
	situationMentionSetList: SituationMentionSet[];
	selectionBox?: React.CSSProperties;
	setDeleteSpanCallback: Dispatch<SetStateAction<Function>>;
	metadata?: AnnotationMetadata;
	activeSituation: UUID[];
	activateStructureIds: (
		uuids: UUID[],
		tokenizationIds: UUID[],
		mentionModel: MentionModel
	) => UUID[];
	activateSituationId: (uuids: UUID[]) => UUID[];
	addToSpanMap: (span: UUID, val: string) => void;
	deleteFromSpanMap: (span: UUID) => void;
	deactivateStructureIds: () => UUID[];
	spanToTypeMap: Map<string, string>;
	setSpanToTypeMap: Dispatch<SetStateAction<Map<string, string>>>;
	setSituationArgumentMap: Dispatch<
		SetStateAction<Map<string, Map<string, Set<string>>>>
	>;
	metaKeyClicked: boolean;
	shiftKeyClicked: boolean;
	addToUserInteractions: (
		action: "create" | "delete" | "set" | "add",
		object: {
			type: "entityMention" | "situationMention" | "entity" | "situation";
			text: string;
		},
		attribute?: {
			type: string;
			value: string;
		},
		type?: string,
		role?: string,
		referent?: string,
		referredSituationMention?: string
	) => void;
	spanMap: Map<string, string>;
	hoveredSpan: string;
	setHoveredSpan: Function;
}
export const TokenizationContext = createContext<TokenizationContextModel>({
	highlightedMentions: new Set<string>(),
	activeTypeIds: [],
	entityMentionSetList: [],
	situationMentionSetList: [],
	metadata: undefined,
	activeSituation: [],
	activateStructureIds: (
		uuids: UUID[],
		tokenizationIds: UUID[],
		mentionModel: MentionModel
	) => [],
	addToSpanMap: (span: UUID, val: string) => {},
	deleteFromSpanMap: (span: UUID) => {},
	setDeleteSpanCallback: () => () => null,
	activateSituationId: (uuids: UUID[]) => [],
	deactivateStructureIds: () => [],
	spanToTypeMap: new Map<string, string>(),
	setSpanToTypeMap: () => null,
	setSituationArgumentMap: () => null,
	metaKeyClicked: false,
	shiftKeyClicked: true,
	addToUserInteractions: (
		action: "create" | "delete" | "set" | "add",
		object: {
			type: "entityMention" | "situationMention" | "entity" | "situation";
			text: string;
		},
		attribute?: {
			type: string;
			value: string;
		},
		type?: string,
		role?: string,
		referent?: string,
		referredSituationMention?: string
	) => null,
	spanMap: new Map<string, string>(),
	hoveredSpan: "",
	setHoveredSpan: Function,
});

export const MentionModelContext = createContext<MentionModel>("entity");

interface ModalOpenContextModel {
	modalOpen: boolean;
	setModalOpen: Dispatch<SetStateAction<boolean>>;
}
export const ModalOpenContext = createContext<ModalOpenContextModel>({
	modalOpen: false,
	setModalOpen: () => null,
});

interface MouseDragTokenContextModel {
	mouseDragging: boolean | undefined;
	setMouseDragging: Dispatch<SetStateAction<boolean | undefined>>;
	shiftMouseDragging: boolean | undefined;
	setShiftMouseDragging: Dispatch<SetStateAction<boolean | undefined>>;
	tokenizationId: UUID;
	activeTokenizationIds: UUID[];
	setDragStartTokenIndex: Dispatch<SetStateAction<number>>;
	setDragEndTokenIndex: Dispatch<SetStateAction<number>>;
	dragStartTokenIndex: number;
	dragEndTokenIndex: number;
}

export const MouseDragTokenContext = createContext<MouseDragTokenContextModel>({
	mouseDragging: false,
	shiftMouseDragging: false,
	tokenizationId: { uuidString: "" },
	activeTokenizationIds: [],
	setMouseDragging: () => null,
	setShiftMouseDragging: () => null,
	setDragStartTokenIndex: () => null,
	setDragEndTokenIndex: () => null,
	dragStartTokenIndex: -1,
	dragEndTokenIndex: -1,
});

interface MentionAnnotationContextModel {
	spanToTypeMap: Map<string, string>;
	setSpanToTypeMap: Dispatch<SetStateAction<Map<string, string>>>;
	situationArgumentMap: Map<string, Map<string, Set<string>>>;
	setSituationArgumentMap: Dispatch<
		SetStateAction<Map<string, Map<string, Set<string>>>>
	>;
	displayToast: (message: string, toastType: ToastType) => void;
	addToUserInteractions: (
		action: "create" | "delete" | "set" | "add",
		object: {
			type: "entityMention" | "situationMention" | "entity" | "situation";
			text: string;
		},
		attribute?: {
			type: string;
			value: string;
		},
		type?: string,
		role?: string,
		referent?: string,
		referredSituationMention?: string
	) => void;
}

export const MentionAnnotationContext =
	createContext<MentionAnnotationContextModel>({
		spanToTypeMap: new Map<string, string>(),
		setSpanToTypeMap: () => null,
		situationArgumentMap: new Map<string, Map<string, Set<string>>>(),
		setSituationArgumentMap: () => null,
		displayToast: (message: string, toastType: ToastType) => null,
		addToUserInteractions: (
			action: "create" | "delete" | "set" | "add",
			object: {
				type:
					| "entityMention"
					| "situationMention"
					| "entity"
					| "situation";
				text: string;
			},
			attribute?: {
				type: string;
				value: string;
			},
			type?: string,
			role?: string,
			referent?: string,
			referredSituationMention?: string
		) => null,
	});
